import { useContext, useEffect, useState } from "react";
import { orgId } from "../utils/dashboardUtils";
import { DisaggregatedMetric, GeoDetails, GeoTag } from "../services/types";

import printWhiteSvg from '../assets/print-white.svg';

import MetricCategoriesContext from "../context/MetricCategoriesContext";
import Header from "../components/pdf/Header";
import Footer from "../components/pdf/Footer";
import PDFMetricCard from "../components/pdf/PDFMetricCard";
import LoadingIndicator from "../components/LoadingIndicator";
import { indicatorsEnv } from "../utils/environment";


interface MetricsModalProps {
    isOpen: boolean;
    onClose: (e: React.MouseEvent<HTMLButtonElement>) => void;
    geoDetails: GeoDetails[];
    geoTag: GeoTag | null;
    pdfUrl: string;
    logoUrl: string;
    legendUrl: string;
    selectedArea: string;
    siteUrl: string;
}

const MetricsModal: React.FC<MetricsModalProps> = ({ isOpen, onClose, geoDetails, geoTag, pdfUrl, logoUrl, legendUrl, selectedArea }) => {
    const { metricCategories } = useContext(MetricCategoriesContext);
    const [loading, setLoading] = useState(true);
    const [disaggregatedData, setDisaggregatedData] = useState<DisaggregatedMetric[]>([]);
    const geoEncoding = 'delta';
    const indicatorsUrl = `${import.meta.env.VITE_DASHBOARD_BACKEND_URL}/api/getallindicatorsv2`;

    const fetchData = async () => {
        if (!isOpen || !orgId || metricCategories.length === 0) return;
    
        try {
            setLoading(true);
    
            if (orgId !== null) {
                // Generate an array of fetch promises, one for each category
                const fetchPromises = metricCategories.map(category => 
                    fetch(indicatorsUrl, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            geoDetails,
                            orgId,
                            dashboardView: category,
                            geoEncoding,
                            geoTag,
                            allowDefault: true,
                            environment: indicatorsEnv
                        }),
                    }).then(response => {
                        if (!response.ok) {
                            throw new Error(`Failed to fetch data for ${category}. Response: ${response}`);
                        }
                        return response.json();
                    })
                );
    
                // Resolve all promises in parallel
                const results = await Promise.all(fetchPromises);
                console.log('results from category calls:', results);
    
                // Combine and categorize the results
                const combinedData = results.flat();
                setDisaggregatedData(combinedData);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            // setError(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen && metricCategories.length > 0) {
            console.log('Effect triggered:', { isOpen, geoDetails, orgId, metricCategories });
            fetchData();
        }
    }, [isOpen, geoDetails, orgId, metricCategories]);

    console.log('disaggreagted data:', disaggregatedData);
    
    // Function to categorize the data based on category
    const categorizeData = (data: DisaggregatedMetric[]) => {
        return metricCategories.map(category => {
            // Filter metrics by category
            const filteredMetrics = data.filter(item => item.category === category);
            return {
                category,
                metrics: filteredMetrics
            };
        });
    };

    const categoryColors: { [key: string]: string }  = {
        Economy: 'bg-color-cai-orange',
        Housing: 'bg-secondary-green',
        Education: 'bg-primary-red',
        Overview: 'bg-secondary-blue'
    };

    // Categorize the disaggregated data
    const categorizedData = categorizeData(disaggregatedData);

    const handlePrint = () => {
        const originalTitle = document.title;
        document.title = "DashboardMetrics";  // Set your desired title here
        window.print();
        document.title = originalTitle;  // Restore the original title
    };

    if (loading) {
        return <LoadingIndicator/>
    }

    return (
        <div id="print-container" className="fixed inset-0 bg-white p-4 overflow-y-auto z-50">
            {/* Wrapper to limit the modal's width */}
            <div className="max-w-[800px] mx-auto w-full mb-8">
                <div className="relative h-full w-full">
                    <button
                        type="button"
                        aria-label="Close modal"
                        onClick={onClose}
                        className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                    {/* Print and Close buttons */}
                    <div className="flex justify-center no-print mb-[1rem]">
                        <button 
                            onClick={handlePrint}
                            className="flex mx-2 bg-color-cai-blue text-white py-2 px-4 rounded-md hover:bg-blue-500"
                        >
                            <img src={printWhiteSvg} alt="Print" className="w-5 h-5 mr-2" />
                            <p className="text-sm">Print</p>
                        </button>
                        <button 
                            onClick={onClose}
                            className="bg-transparent border border-color-cai-lightGray text-color-cai-dashboard py-2 px-4 rounded-md hover:bg-color-cai-lightGray"
                        >
                            <p className="text-sm">Close</p>
                        </button>
                    </div>

                    {/* Page 1 */}
                    <div className="flex flex-col page">
                        <div className="flex flex-col items-center justify-center relative">
                            <div className="w-full h-auto p-4 flex justify-between items-center bg-color-cai-blue">
                                <div className="flex items-center justify-start mx-1 w-full">
                                    <p className="text-[#E8E8E8] text-[32px] font-normal mr-8">Client Map</p>
                                    <p className="pl-8 text-sm font-normal text-white text-opacity-80 border-l border-white border-opacity-20">Lorem ipsum dolor sit amet, consectetur adipiscing elit. <br></br>Vivamus auctor eros vel nisi scelerisque, non hendrerit magna scelerisque. <br></br>Nulla facilisi. Vestibulum et felis a neque malesuada posuere.</p>
                                </div>
                            </div>
                        </div>
                        <div className="flex-grow">
                            <div className="relative inline-block">
                                {pdfUrl && <img src={pdfUrl} className="h-auto w-auto" alt="Esri Map Image" /> }
                                {/* Removing legend for now due to performance concerns */}
                                { <div className="flex justify-between">
                                    { legendUrl && <img src={legendUrl} className="h-[188px] w-[220px] ml-6 mt-[-5rem]" alt="Esri Legend Image" /> }
                                    { logoUrl && <img src={logoUrl} className="h-10 w-30 mr-8 mt-[-1rem]" alt="Esri Logo Image" /> }
                                </div> }
    
                            </div>
                        </div>
                        <Footer/>
                    </div>
                    <div>
                        {/* Loop through each category and create a page for each */}
                        {categorizedData.map(({ category, metrics }) => (
                            <div className="page" key={category}>
                                <Header selectedArea={selectedArea} bgColor={categoryColors[category] || 'bg-color-cai-blue'} title="Client Map" text={category} />
                                <div className="content">
                                    <div className="grid grid-cols-3 gap-2 justify-center ml-16">
                                        {/* Display metric cards for this category */}
                                        {metrics.map((metric) => (
                                            <PDFMetricCard
                                                key={metric.id}
                                                metric={metric}
                                                width="w-full"
                                                svg={undefined}
                                                showMetadataIcon={false}
                                                disaggData={disaggregatedData}
                                            />
                                        ))}
                                    </div>
                                </div>
                                <Footer />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MetricsModal;